'use client'

import ConfirmModal from '@/components/confirm-modal'
import { formatDateOnly } from '@/lib/utils/date-only'
import { UserSelector } from '@/components/UserSelector'
import {
    getHrEmployee,
    getHrEmployeeHistory,
    linkHrEmployeePlatformAccount,
    linkHrEmployeeUser,
    terminateHrEmployee,
    unlinkHrEmployeePlatformAccount,
    unlinkHrEmployeeUser,
    HrEmployee,
} from '@/lib/api/hr/hr-employee-api'
import {
    getDependents,
    createDependent,
    updateDependent,
    deleteDependent,
    HrDependent,
} from '@/lib/api/hr/hr-dependent-api'
import { useResourcePermissions } from '@/lib/hooks/use-resource-permissions'
import { useAuth } from '@/store/hooks'
import { useEffect, useState } from 'react'
import {
    MdAccountBalanceWallet,
    MdAdd,
    MdArrowBack,
    MdBadge,
    MdDelete,
    MdEdit,
    MdExitToApp,
    MdHistory,
    MdLink,
    MdLinkOff,
    MdPeople,
    MdPerson,
    MdVpnKey,
    MdWork,
} from 'react-icons/md'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'

const STATUS_LABELS: Record<string, string> = {
    ACTIVE: 'Ativo',
    ON_LEAVE: 'Afastado',
    SUSPENDED: 'Suspenso',
    TERMINATED: 'Desligado',
}

const STATUS_BADGE: Record<string, string> = {
    ACTIVE: 'badge-success text-white',
    ON_LEAVE: 'badge-warning',
    SUSPENDED: 'badge-error text-white',
    TERMINATED: 'badge-ghost',
}

const GENDER_LABELS: Record<string, string> = {
    MALE: 'Masculino',
    FEMALE: 'Feminino',
    OTHER: 'Outro',
}

const MARITAL_STATUS_LABELS: Record<string, string> = {
    SINGLE: 'Solteiro(a)',
    MARRIED: 'Casado(a)',
    DIVORCED: 'Divorciado(a)',
    WIDOWED: 'Viúvo(a)',
    SEPARATED: 'Separado(a)',
    STABLE_UNION: 'União Estável',
}

const RACE_LABELS: Record<string, string> = {
    WHITE: 'Branca',
    BLACK: 'Preta',
    BROWN: 'Parda',
    ASIAN: 'Amarela',
    INDIGENOUS: 'Indígena',
    NOT_DECLARED: 'Não declarada',
}

const CONTRACT_TYPE_LABELS: Record<string, string> = {
    CLT: 'CLT',
    TEMPORARY: 'Temporário',
    INTERMITTENT: 'Intermitente',
    APPRENTICE: 'Aprendiz',
    INTERN: 'Estagiário',
}

const WORK_REGIME_LABELS: Record<string, string> = {
    ONSITE: 'Presencial',
    REMOTE: 'Remoto',
    HYBRID: 'Híbrido',
}

const SALARY_TYPE_LABELS: Record<string, string> = {
    MONTHLY: 'Mensal',
    HOURLY: 'Horista',
    COMMISSION: 'Comissionista',
}

const BANK_ACCOUNT_TYPE_LABELS: Record<string, string> = {
    CHECKING: 'Corrente',
    SAVINGS: 'Poupança',
    SALARY: 'Salário',
}

const PIX_KEY_TYPE_LABELS: Record<string, string> = {
    CPF: 'CPF',
    CNPJ: 'CNPJ',
    EMAIL: 'E-mail',
    PHONE: 'Telefone',
    RANDOM: 'Aleatória',
}

const TERMINATION_TYPE_LABELS: Record<string, string> = {
    WITHOUT_CAUSE: 'Sem justa causa',
    WITH_CAUSE: 'Com justa causa',
    VOLUNTARY: 'Pedido de demissão',
    MUTUAL_AGREEMENT: 'Acordo mútuo',
    END_OF_CONTRACT: 'Término de contrato',
    DEATH: 'Falecimento',
}

const CHANGE_TYPE_LABELS: Record<string, string> = {
    ADMISSION: 'Admissão',
    PROMOTION: 'Promoção',
    SALARY_CHANGE: 'Alteração salarial',
    TRANSFER: 'Transferência',
    TERMINATION: 'Desligamento',
    CONTRACT_CHANGE: 'Alteração contratual',
    DEPARTMENT_CHANGE: 'Mudança de departamento',
}

const RELATIONSHIP_LABELS: Record<string, string> = {
    SPOUSE: 'Cônjuge',
    CHILD: 'Filho(a)',
    STEPCHILD: 'Enteado(a)',
    PARENT: 'Pai/Mãe',
    SIBLING: 'Irmão/Irmã',
    GUARDIAN: 'Tutelado(a)',
    OTHER: 'Outro',
}

const TABS = [
    { key: 'data', label: 'Dados', icon: MdPerson },
    { key: 'dependents', label: 'Dependentes', icon: MdPeople },
    { key: 'history', label: 'Histórico', icon: MdHistory },
    { key: 'vacations', label: 'Férias', icon: MdWork },
    { key: 'payslips', label: 'Holerites', icon: MdBadge },
    { key: 'access', label: 'Acesso ao sistema', icon: MdVpnKey },
]

export default function HrEmployeeDetailPage() {
    const { user } = useAuth()
    const navigate = useNavigate()
    const { id } = useParams<{ id: string }>()
    const { canUpdate, canDelete } = useResourcePermissions('hr', 'employees')

    const [employee, setEmployee] = useState<HrEmployee | null>(null)
    const [loading, setLoading] = useState(true)
    const [activeTab, setActiveTab] = useState('data')

    // Dependents
    const [dependents, setDependents] = useState<HrDependent[]>([])
    const [loadingDependents, setLoadingDependents] = useState(false)
    const [showDependentModal, setShowDependentModal] = useState(false)
    const [editingDependent, setEditingDependent] = useState<HrDependent | null>(null)
    const [savingDependent, setSavingDependent] = useState(false)
    const [showDeleteDependentModal, setShowDeleteDependentModal] = useState(false)
    const [dependentToDelete, setDependentToDelete] = useState<HrDependent | null>(null)
    const [deletingDependent, setDeletingDependent] = useState(false)
    const [dependentForm, setDependentForm] = useState({
        name: '',
        cpf: '',
        birthDate: '',
        relationship: '',
        irDeduction: false,
        familySalary: false,
        healthPlan: false,
    })

    // History
    const [history, setHistory] = useState<any[]>([])
    const [loadingHistory, setLoadingHistory] = useState(false)

    // Acesso ao sistema
    const [selectedUserId, setSelectedUserId] = useState<number | null>(null)
    const [linkingUser, setLinkingUser] = useState(false)
    const [showUnlinkModal, setShowUnlinkModal] = useState(false)
    const [unlinkingUser, setUnlinkingUser] = useState(false)

    // Conta da plataforma (destino do salário)
    const [linkingAccount, setLinkingAccount] = useState(false)
    const [showUnlinkAccountModal, setShowUnlinkAccountModal] = useState(false)
    const [unlinkingAccount, setUnlinkingAccount] = useState(false)

    // Terminate
    const [showTerminateModal, setShowTerminateModal] = useState(false)
    const [terminating, setTerminating] = useState(false)
    const [terminateForm, setTerminateForm] = useState({
        terminationType: '',
        terminationReason: '',
        terminationDate: '',
    })

    useEffect(() => {
        if (id) {
            loadEmployee()
        }
    }, [id])

    useEffect(() => {
        if (id && activeTab === 'dependents') {
            loadDependents()
        }
        if (id && activeTab === 'history') {
            loadHistory()
        }
    }, [id, activeTab])

    const loadEmployee = async () => {
        if (!id) return

        setLoading(true)
        try {
            const response = await getHrEmployee(parseInt(id, 10))
            if (response.success && response.result) {
                setEmployee(response.result)
            } else {
                toast.error('Funcionário não encontrado')
                navigate('/hr/funcionarios')
            }
        } catch {
            toast.error('Erro ao carregar funcionário')
            navigate('/hr/funcionarios')
        } finally {
            setLoading(false)
        }
    }

    const loadDependents = async () => {
        if (!id) return

        setLoadingDependents(true)
        try {
            const response = await getDependents(parseInt(id, 10))
            if (response.success && response.result) {
                setDependents(Array.isArray(response.result) ? response.result : [])
            }
        } catch {
            toast.error('Erro ao carregar dependentes')
        } finally {
            setLoadingDependents(false)
        }
    }

    const loadHistory = async () => {
        if (!id) return

        setLoadingHistory(true)
        try {
            const response = await getHrEmployeeHistory(parseInt(id, 10))
            if (response.success && response.result) {
                setHistory(Array.isArray(response.result) ? response.result : [])
            }
        } catch {
            toast.error('Erro ao carregar histórico')
        } finally {
            setLoadingHistory(false)
        }
    }

    const handleLinkUser = async () => {
        if (!id || !selectedUserId) {
            toast.warning('Selecione um usuário')
            return
        }

        setLinkingUser(true)
        try {
            const response = await linkHrEmployeeUser(parseInt(id, 10), selectedUserId)
            if (response.success) {
                toast.success('Usuário vinculado com sucesso')
                setSelectedUserId(null)
                loadEmployee()
            } else {
                toast.error(response.error || 'Erro ao vincular usuário')
            }
        } catch {
            toast.error('Erro ao vincular usuário')
        } finally {
            setLinkingUser(false)
        }
    }

    const handleUnlinkUser = async () => {
        if (!id) return

        setUnlinkingUser(true)
        try {
            const response = await unlinkHrEmployeeUser(parseInt(id, 10))
            if (response.success) {
                toast.success('Usuário desvinculado com sucesso')
                setShowUnlinkModal(false)
                loadEmployee()
            } else {
                toast.error(response.error || 'Erro ao desvincular usuário')
            }
        } catch {
            toast.error('Erro ao desvincular usuário')
        } finally {
            setUnlinkingUser(false)
        }
    }

    const handleLinkPlatformAccount = async () => {
        if (!id) return

        setLinkingAccount(true)
        try {
            const response = await linkHrEmployeePlatformAccount(parseInt(id, 10))
            if (response.success) {
                toast.success('Conta da plataforma vinculada com sucesso')
                loadEmployee()
            } else {
                toast.error(response.error || 'Erro ao vincular conta da plataforma')
            }
        } catch {
            toast.error('Erro ao vincular conta da plataforma')
        } finally {
            setLinkingAccount(false)
        }
    }

    const handleUnlinkPlatformAccount = async () => {
        if (!id) return

        setUnlinkingAccount(true)
        try {
            const response = await unlinkHrEmployeePlatformAccount(parseInt(id, 10))
            if (response.success) {
                toast.success('Conta da plataforma desvinculada')
                setShowUnlinkAccountModal(false)
                loadEmployee()
            } else {
                toast.error(response.error || 'Erro ao desvincular conta da plataforma')
            }
        } catch {
            toast.error('Erro ao desvincular conta da plataforma')
        } finally {
            setUnlinkingAccount(false)
        }
    }

    const formatCurrency = (cents: number | null) => {
        if (cents == null) return '-'
        return (cents / 100).toLocaleString('pt-BR', {
            style: 'currency',
            currency: 'BRL',
        })
    }

    // Nascimento, admissão, demissão, emissão de CTPS e vigência são DATAS, não
    // instantes: formatar com `new Date()` recuava um dia no fuso de Brasília.
    const formatDate = (date: string | null | undefined) => formatDateOnly(date)

    const openDependentModal = (dep?: HrDependent) => {
        if (dep) {
            setEditingDependent(dep)
            setDependentForm({
                name: dep.name || '',
                cpf: dep.cpf || '',
                birthDate: dep.birthDate ? dep.birthDate.substring(0, 10) : '',
                relationship: dep.relationship || '',
                irDeduction: dep.irDeduction || false,
                familySalary: dep.familySalary || false,
                healthPlan: dep.healthPlan || false,
            })
        } else {
            setEditingDependent(null)
            setDependentForm({
                name: '',
                cpf: '',
                birthDate: '',
                relationship: '',
                irDeduction: false,
                familySalary: false,
                healthPlan: false,
            })
        }
        setShowDependentModal(true)
    }

    const closeDependentModal = () => {
        setShowDependentModal(false)
        setEditingDependent(null)
    }

    const handleSaveDependent = async () => {
        if (!id) return
        if (!dependentForm.name.trim()) {
            toast.warning('Preencha o nome do dependente')
            return
        }
        if (!dependentForm.relationship) {
            toast.warning('Selecione o parentesco')
            return
        }

        setSavingDependent(true)
        try {
            const payload = {
                name: dependentForm.name.trim(),
                cpf: dependentForm.cpf || null,
                birthDate: dependentForm.birthDate || null,
                relationship: dependentForm.relationship,
                irDeduction: dependentForm.irDeduction,
                familySalary: dependentForm.familySalary,
                healthPlan: dependentForm.healthPlan,
            }

            let response
            if (editingDependent) {
                response = await updateDependent(
                    parseInt(id, 10),
                    editingDependent.id,
                    payload
                )
            } else {
                response = await createDependent(parseInt(id, 10), payload)
            }

            if (response.success) {
                toast.success(
                    editingDependent
                        ? 'Dependente atualizado com sucesso'
                        : 'Dependente adicionado com sucesso'
                )
                closeDependentModal()
                await loadDependents()
            } else {
                toast.error(response.error || 'Erro ao salvar dependente')
            }
        } catch {
            toast.error('Erro ao salvar dependente')
        } finally {
            setSavingDependent(false)
        }
    }

    const handleDeleteDependent = async () => {
        if (!id || !dependentToDelete) return

        setDeletingDependent(true)
        try {
            const response = await deleteDependent(
                parseInt(id, 10),
                dependentToDelete.id
            )
            if (response.success) {
                toast.success('Dependente excluído com sucesso')
                setShowDeleteDependentModal(false)
                setDependentToDelete(null)
                await loadDependents()
            } else {
                toast.error(response.error || 'Erro ao excluir dependente')
            }
        } catch {
            toast.error('Erro ao excluir dependente')
        } finally {
            setDeletingDependent(false)
        }
    }

    const handleTerminate = async () => {
        if (!id) return
        if (!terminateForm.terminationType) {
            toast.warning('Selecione o tipo de desligamento')
            return
        }
        if (!terminateForm.terminationDate) {
            toast.warning('Informe a data de desligamento')
            return
        }

        setTerminating(true)
        try {
            const response = await terminateHrEmployee(parseInt(id, 10), {
                terminationType: terminateForm.terminationType,
                terminationReason: terminateForm.terminationReason || undefined,
                terminationDate: terminateForm.terminationDate,
            })
            if (response.success) {
                toast.success('Funcionário desligado com sucesso')
                setShowTerminateModal(false)
                await loadEmployee()
            } else {
                toast.error(response.error || 'Erro ao desligar funcionário')
            }
        } catch {
            toast.error('Erro ao desligar funcionário')
        } finally {
            setTerminating(false)
        }
    }

    const InfoItem = ({
        label,
        value,
    }: {
        label: string
        value: string | null | undefined
    }) => {
        if (!value || value === '-') return null
        return (
            <div>
                <p className="text-xs text-base-content/50">{label}</p>
                <p className="font-medium text-base-content">{value}</p>
            </div>
        )
    }

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">
                    Carregando funcionário...
                </p>
            </div>
        )
    }

    if (!employee) return null

    const emp = employee as any

    return (
        <div className="flex flex-col gap-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <button
                        onClick={() => navigate('/hr/funcionarios')}
                        className="btn btn-ghost btn-circle btn-sm"
                    >
                        <MdArrowBack className="text-xl" />
                    </button>
                    <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-neutral to-neutral/70 flex items-center justify-center shadow-lg">
                            <MdPerson className="text-2xl text-white" />
                        </div>
                        <div>
                            <h1 className="text-2xl font-bold text-base-content">
                                {employee.name}
                            </h1>
                            {employee.jobTitle && (
                                <p className="text-sm text-base-content/60">
                                    {employee.jobTitle}
                                </p>
                            )}
                        </div>
                    </div>
                </div>

                <div className="flex items-center gap-2">
                    <span
                        className={`badge ${
                            STATUS_BADGE[employee.status] || 'badge-ghost'
                        }`}
                    >
                        {STATUS_LABELS[employee.status] || employee.status}
                    </span>
                    {canUpdate && (
                        <button
                            onClick={() =>
                                navigate(`/hr/funcionarios/${employee.id}/edit`)
                            }
                            className="btn btn-secondary btn-sm gap-2"
                        >
                            <MdEdit className="text-lg" />
                            <span className="hidden sm:inline">Editar</span>
                        </button>
                    )}
                    {canUpdate && employee.status !== 'TERMINATED' && (
                        <button
                            onClick={() => setShowTerminateModal(true)}
                            className="btn btn-error btn-sm gap-2"
                        >
                            <MdExitToApp className="text-lg" />
                            <span className="hidden sm:inline">Desligar</span>
                        </button>
                    )}
                </div>
            </div>

            {/* Tabs */}
            <div role="tablist" className="tabs tabs-bordered">
                {TABS.map(tab => (
                    <button
                        key={tab.key}
                        role="tab"
                        className={`tab gap-2 ${activeTab === tab.key ? 'tab-active' : ''}`}
                        onClick={() => setActiveTab(tab.key)}
                    >
                        <tab.icon className="text-lg" />
                        {tab.label}
                    </button>
                ))}
            </div>

            {/* Tab: Dados */}
            {activeTab === 'data' && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Dados Pessoais */}
                    <div className="bg-base-100 rounded-xl border border-base-300/50 p-6">
                        <h2 className="text-lg font-semibold text-base-content mb-4">
                            Dados Pessoais
                        </h2>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <InfoItem label="Nome" value={employee.name} />
                            <InfoItem label="CPF" value={employee.cpf} />
                            <InfoItem label="RG" value={employee.rg} />
                            <InfoItem
                                label="Órgão emissor"
                                value={
                                    [emp.rgIssuer, emp.rgState]
                                        .filter(Boolean)
                                        .join(' / ') || null
                                }
                            />
                            <InfoItem
                                label="Data de nascimento"
                                value={formatDate(employee.birthDate)}
                            />
                            <InfoItem
                                label="Gênero"
                                value={
                                    employee.gender
                                        ? GENDER_LABELS[employee.gender] || employee.gender
                                        : null
                                }
                            />
                            <InfoItem
                                label="Estado civil"
                                value={
                                    employee.maritalStatus
                                        ? MARITAL_STATUS_LABELS[employee.maritalStatus] ||
                                          employee.maritalStatus
                                        : null
                                }
                            />
                            <InfoItem label="Nacionalidade" value={employee.nationality} />
                            <InfoItem label="Cidade natal" value={emp.birthCity} />
                            <InfoItem label="Estado natal" value={emp.birthState} />
                            <InfoItem label="Nome da mãe" value={emp.motherName} />
                            <InfoItem label="Nome do pai" value={emp.fatherName} />
                            <InfoItem
                                label="Raça/Cor"
                                value={
                                    employee.race
                                        ? RACE_LABELS[employee.race] || employee.race
                                        : null
                                }
                            />
                        </div>
                    </div>

                    {/* Contato */}
                    <div className="bg-base-100 rounded-xl border border-base-300/50 p-6">
                        <h2 className="text-lg font-semibold text-base-content mb-4">
                            Contato
                        </h2>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <InfoItem label="E-mail" value={employee.email} />
                            <InfoItem label="Telefone" value={employee.phone} />
                            <InfoItem label="Telefone 2" value={emp.phone2} />
                            <InfoItem label="WhatsApp" value={employee.whatsAppNumber} />
                        </div>
                    </div>

                    {/* Documentos */}
                    <div className="bg-base-100 rounded-xl border border-base-300/50 p-6">
                        <h2 className="text-lg font-semibold text-base-content mb-4">
                            Documentos
                        </h2>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <InfoItem label="PIS/PASEP" value={employee.pis} />
                            <InfoItem label="CTPS - Número" value={emp.ctpsNumber} />
                            <InfoItem label="CTPS - Série" value={emp.ctpsSeries} />
                            <InfoItem label="CTPS - UF" value={emp.ctpsState} />
                            <InfoItem
                                label="CTPS - Emissão"
                                value={formatDate(emp.ctpsIssueDate)}
                            />
                            <InfoItem label="Título de eleitor" value={emp.voterTitle} />
                            <InfoItem
                                label="Certificado militar"
                                value={emp.militaryCertificate}
                            />
                        </div>
                    </div>

                    {/* Endereço */}
                    <div className="bg-base-100 rounded-xl border border-base-300/50 p-6">
                        <h2 className="text-lg font-semibold text-base-content mb-4">
                            Endereço
                        </h2>
                        {(() => {
                            const parts = [
                                emp.street,
                                emp.number,
                                emp.complement,
                                emp.neighborhood,
                                employee.city,
                                employee.state,
                                employee.zipCode,
                            ].filter(Boolean)
                            return parts.length > 0 ? (
                                <p className="text-base-content">{parts.join(', ')}</p>
                            ) : (
                                <p className="text-base-content/50 text-sm">
                                    Nenhum endereço cadastrado
                                </p>
                            )
                        })()}
                    </div>

                    {/* Dados Bancários */}
                    <div className="bg-base-100 rounded-xl border border-base-300/50 p-6">
                        <h2 className="text-lg font-semibold text-base-content mb-4">
                            Dados Bancários
                        </h2>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <InfoItem
                                label="Banco"
                                value={
                                    employee.bankName
                                        ? `${emp.bankCode ? emp.bankCode + ' - ' : ''}${employee.bankName}`
                                        : null
                                }
                            />
                            <InfoItem label="Agência" value={employee.bankAgency} />
                            <InfoItem label="Conta" value={employee.bankAccount} />
                            <InfoItem
                                label="Tipo de conta"
                                value={
                                    emp.bankAccountType
                                        ? BANK_ACCOUNT_TYPE_LABELS[emp.bankAccountType] ||
                                          emp.bankAccountType
                                        : null
                                }
                            />
                            <InfoItem
                                label={`Chave PIX${
                                    emp.pixKeyType
                                        ? ` (${PIX_KEY_TYPE_LABELS[emp.pixKeyType] || emp.pixKeyType})`
                                        : ''
                                }`}
                                value={emp.pixKey}
                            />
                        </div>

                        {/* Conta da plataforma — destino do salário */}
                        <div className="mt-6 pt-4 border-t border-base-300/50">
                            <div className="flex items-center gap-2 mb-1">
                                <MdAccountBalanceWallet className="text-lg text-primary" />
                                <h3 className="font-semibold text-base-content">
                                    Conta na plataforma
                                </h3>
                            </div>
                            <p className="text-sm text-base-content/60 mb-4">
                                Com a conta do funcionário vinculada, o salário sai por
                                transferência entre contas — sem tarifa e imediata. Sem ela, o
                                pagamento usa o PIX ou os dados bancários acima.
                            </p>

                            {employee.platformOrganization ? (
                                <div className="flex flex-col gap-3">
                                    <div className="flex items-center gap-4 p-4 bg-base-200/50 rounded-xl border border-base-300/50">
                                        <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                                            <MdAccountBalanceWallet className="text-2xl text-primary" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="font-semibold text-base-content truncate">
                                                {employee.platformOrganization.name}
                                            </p>
                                            <p className="text-xs text-base-content/60">
                                                CPF {employee.platformOrganization.cpf || '-'}
                                            </p>
                                        </div>
                                        {employee.platformOrganization.paymentWalletId ? (
                                            <span className="badge badge-success text-white">
                                                Pronta para receber
                                            </span>
                                        ) : (
                                            <span
                                                className="badge badge-warning"
                                                title="A conta ainda não tem carteira ativa no provedor. Enquanto isso, o salário sai pelo PIX/TED do cadastro."
                                            >
                                                Aguardando ativação
                                            </span>
                                        )}
                                    </div>

                                    {canUpdate && (
                                        <div>
                                            <button
                                                type="button"
                                                className="btn btn-outline btn-error btn-sm gap-2"
                                                onClick={() => setShowUnlinkAccountModal(true)}
                                            >
                                                <MdLinkOff className="text-lg" />
                                                Desvincular conta
                                            </button>
                                        </div>
                                    )}
                                </div>
                            ) : canUpdate ? (
                                <div className="flex flex-col gap-3">
                                    <p className="text-sm text-base-content/70">
                                        Nenhuma conta vinculada. A busca usa o CPF do funcionário
                                        {employee.cpf ? ` (${employee.cpf})` : ''}.
                                    </p>
                                    <div>
                                        <button
                                            type="button"
                                            className="btn btn-primary btn-sm gap-2"
                                            onClick={handleLinkPlatformAccount}
                                            disabled={linkingAccount || !employee.cpf}
                                        >
                                            {linkingAccount ? (
                                                <span className="loading loading-spinner loading-sm" />
                                            ) : (
                                                <MdLink className="text-lg" />
                                            )}
                                            Vincular conta da plataforma
                                        </button>
                                    </div>
                                    {!employee.cpf && (
                                        <p className="text-xs text-warning">
                                            Preencha o CPF do funcionário para localizar a conta.
                                        </p>
                                    )}
                                </div>
                            ) : (
                                <p className="text-sm text-base-content/50">
                                    Nenhuma conta da plataforma vinculada.
                                </p>
                            )}
                        </div>
                    </div>

                    {/* Contrato */}
                    <div className="bg-base-100 rounded-xl border border-base-300/50 p-6">
                        <h2 className="text-lg font-semibold text-base-content mb-4">
                            Dados do Contrato
                        </h2>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <InfoItem
                                label="Data de admissão"
                                value={formatDate(employee.admissionDate)}
                            />
                            <InfoItem
                                label="Tipo de contrato"
                                value={
                                    CONTRACT_TYPE_LABELS[employee.contractType] ||
                                    employee.contractType
                                }
                            />
                            <InfoItem
                                label="Regime de trabalho"
                                value={
                                    WORK_REGIME_LABELS[employee.workRegime] ||
                                    employee.workRegime
                                }
                            />
                            <InfoItem
                                label="Carga horária semanal"
                                value={
                                    employee.weeklyHours != null
                                        ? `${employee.weeklyHours}h`
                                        : null
                                }
                            />
                            <InfoItem label="Cargo" value={employee.jobTitle} />
                            <InfoItem label="Função" value={emp.jobFunction} />
                            <InfoItem label="CBO" value={employee.cboCode} />
                            <InfoItem label="Departamento" value={employee.department} />
                            <InfoItem
                                label="Salário"
                                value={formatCurrency(employee.salary)}
                            />
                            <InfoItem
                                label="Tipo de salário"
                                value={
                                    SALARY_TYPE_LABELS[employee.salaryType] ||
                                    employee.salaryType
                                }
                            />
                            <InfoItem
                                label="Dias de experiência"
                                value={
                                    emp.experienceDays != null
                                        ? `${emp.experienceDays} dias`
                                        : null
                                }
                            />
                            {employee.terminationDate && (
                                <InfoItem
                                    label="Data de desligamento"
                                    value={formatDate(employee.terminationDate)}
                                />
                            )}
                            {employee.terminationType && (
                                <InfoItem
                                    label="Tipo de desligamento"
                                    value={
                                        TERMINATION_TYPE_LABELS[employee.terminationType] ||
                                        employee.terminationType
                                    }
                                />
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Tab: Dependentes */}
            {activeTab === 'dependents' && (
                <div className="bg-base-100 rounded-xl border border-base-300/50 p-6">
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="text-lg font-semibold text-base-content">
                            Dependentes
                        </h2>
                        {canUpdate && (
                            <button
                                className="btn btn-secondary btn-sm gap-2"
                                onClick={() => openDependentModal()}
                            >
                                <MdAdd className="text-lg" />
                                Adicionar
                            </button>
                        )}
                    </div>

                    {loadingDependents ? (
                        <div className="flex items-center justify-center py-8">
                            <span className="loading loading-spinner loading-md text-secondary" />
                        </div>
                    ) : dependents.length === 0 ? (
                        <p className="text-base-content/50 text-sm py-4">
                            Nenhum dependente cadastrado.
                        </p>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="table table-sm">
                                <thead>
                                    <tr>
                                        <th>Nome</th>
                                        <th>CPF</th>
                                        <th>Nascimento</th>
                                        <th>Parentesco</th>
                                        <th>IR</th>
                                        <th>Sal. Família</th>
                                        <th>Plano</th>
                                        {canUpdate && <th></th>}
                                    </tr>
                                </thead>
                                <tbody>
                                    {dependents.map(dep => (
                                        <tr key={dep.id} className="hover">
                                            <td className="font-medium">{dep.name}</td>
                                            <td>{dep.cpf || '-'}</td>
                                            <td>{formatDate(dep.birthDate)}</td>
                                            <td>
                                                {RELATIONSHIP_LABELS[dep.relationship] ||
                                                    dep.relationship}
                                            </td>
                                            <td>
                                                <span
                                                    className={`badge badge-xs ${
                                                        dep.irDeduction
                                                            ? 'badge-success text-white'
                                                            : 'badge-ghost'
                                                    }`}
                                                >
                                                    {dep.irDeduction ? 'Sim' : 'Não'}
                                                </span>
                                            </td>
                                            <td>
                                                <span
                                                    className={`badge badge-xs ${
                                                        dep.familySalary
                                                            ? 'badge-success text-white'
                                                            : 'badge-ghost'
                                                    }`}
                                                >
                                                    {dep.familySalary ? 'Sim' : 'Não'}
                                                </span>
                                            </td>
                                            <td>
                                                <span
                                                    className={`badge badge-xs ${
                                                        dep.healthPlan
                                                            ? 'badge-success text-white'
                                                            : 'badge-ghost'
                                                    }`}
                                                >
                                                    {dep.healthPlan ? 'Sim' : 'Não'}
                                                </span>
                                            </td>
                                            {canUpdate && (
                                                <td>
                                                    <div className="flex gap-1">
                                                        <button
                                                            className="btn btn-ghost btn-xs"
                                                            onClick={() =>
                                                                openDependentModal(dep)
                                                            }
                                                        >
                                                            <MdEdit className="text-base" />
                                                        </button>
                                                        <button
                                                            className="btn btn-ghost btn-xs text-error"
                                                            onClick={() => {
                                                                setDependentToDelete(dep)
                                                                setShowDeleteDependentModal(true)
                                                            }}
                                                        >
                                                            <MdDelete className="text-base" />
                                                        </button>
                                                    </div>
                                                </td>
                                            )}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}

            {/* Tab: Histórico */}
            {activeTab === 'history' && (
                <div className="bg-base-100 rounded-xl border border-base-300/50 p-6">
                    <h2 className="text-lg font-semibold text-base-content mb-4">
                        Histórico de Alterações
                    </h2>

                    {loadingHistory ? (
                        <div className="flex items-center justify-center py-8">
                            <span className="loading loading-spinner loading-md text-secondary" />
                        </div>
                    ) : history.length === 0 ? (
                        <p className="text-base-content/50 text-sm py-4">
                            Nenhum registro no histórico.
                        </p>
                    ) : (
                        <div className="relative">
                            <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-base-300"></div>
                            <div className="space-y-6">
                                {history.map((entry: any, index: number) => (
                                    <div key={entry.id || index} className="relative pl-10">
                                        <div className="absolute left-2.5 top-1 w-3 h-3 rounded-full bg-secondary border-2 border-base-100"></div>
                                        <div className="bg-base-200/50 rounded-lg p-4">
                                            <div className="flex items-center justify-between mb-1">
                                                <span className="font-semibold text-sm text-base-content">
                                                    {CHANGE_TYPE_LABELS[entry.changeType] ||
                                                        entry.changeType}
                                                </span>
                                                <span className="text-xs text-base-content/50">
                                                    {formatDate(entry.effectiveDate)}
                                                </span>
                                            </div>
                                            {entry.description && (
                                                <p className="text-sm text-base-content/70">
                                                    {entry.description}
                                                </p>
                                            )}
                                            {entry.createdBy && (
                                                <p className="text-xs text-base-content/40 mt-1">
                                                    por {entry.createdBy.name || 'Sistema'}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Tab: Férias (placeholder) */}
            {activeTab === 'vacations' && (
                <div className="bg-base-100 rounded-xl border border-base-300/50 p-6">
                    <h2 className="text-lg font-semibold text-base-content mb-4">
                        Férias
                    </h2>
                    <p className="text-base-content/50 text-sm py-8 text-center">
                        Módulo de férias em desenvolvimento.
                    </p>
                </div>
            )}

            {/* Tab: Holerites (placeholder) */}
            {activeTab === 'payslips' && (
                <div className="bg-base-100 rounded-xl border border-base-300/50 p-6">
                    <h2 className="text-lg font-semibold text-base-content mb-4">
                        Holerites
                    </h2>
                    <p className="text-base-content/50 text-sm py-8 text-center">
                        Módulo de holerites em desenvolvimento.
                    </p>
                </div>
            )}

            {/* Tab: Acesso ao sistema */}
            {activeTab === 'access' && (
                <div className="bg-base-100 rounded-xl border border-base-300/50 p-6">
                    <div className="flex items-center gap-3 mb-1">
                        <MdVpnKey className="text-xl text-primary" />
                        <h2 className="text-lg font-semibold text-base-content">
                            Acesso ao sistema
                        </h2>
                    </div>
                    <p className="text-sm text-base-content/60 mb-6">
                        Vincule um usuário para que este funcionário acesse a plataforma. O
                        vínculo é opcional — funcionário sem usuário continua no cadastro e na
                        folha, apenas não faz login.
                    </p>

                    {employee?.user ? (
                        <div className="flex flex-col gap-4">
                            <div className="flex items-center gap-4 p-4 bg-base-200/50 rounded-xl border border-base-300/50">
                                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                                    <MdPerson className="text-2xl text-primary" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="font-semibold text-base-content truncate">
                                        {employee.user.name}
                                    </p>
                                    {employee.user.email && (
                                        <p className="text-sm text-base-content/60 truncate">
                                            {employee.user.email}
                                        </p>
                                    )}
                                </div>
                                <span className="badge badge-success text-white gap-1">
                                    <MdLink className="text-sm" />
                                    Vinculado
                                </span>
                            </div>

                            {canUpdate && (
                                <div>
                                    <button
                                        type="button"
                                        className="btn btn-outline btn-error gap-2"
                                        onClick={() => setShowUnlinkModal(true)}
                                    >
                                        <MdLinkOff className="text-lg" />
                                        Desvincular usuário
                                    </button>
                                </div>
                            )}
                        </div>
                    ) : canUpdate ? (
                        <div className="flex flex-col gap-4 max-w-xl">
                            <UserSelector
                                value={selectedUserId}
                                onChange={userId => setSelectedUserId(userId)}
                                label="Usuário do sistema"
                                placeholder="Buscar usuário por nome ou e-mail..."
                                disabled={linkingUser}
                            />
                            <div>
                                <button
                                    type="button"
                                    className="btn btn-primary gap-2"
                                    onClick={handleLinkUser}
                                    disabled={!selectedUserId || linkingUser}
                                >
                                    {linkingUser ? (
                                        <span className="loading loading-spinner loading-sm" />
                                    ) : (
                                        <MdLink className="text-lg" />
                                    )}
                                    Vincular usuário
                                </button>
                            </div>
                        </div>
                    ) : (
                        <p className="text-base-content/50 text-sm py-8 text-center">
                            Nenhum usuário vinculado a este funcionário.
                        </p>
                    )}
                </div>
            )}

            {/* Dependent Modal */}
            <dialog
                className="modal"
                open={showDependentModal}
            >
                <div className="modal-box w-11/12 max-w-xl">
                    <h3 className="font-bold text-lg mb-4">
                        {editingDependent
                            ? 'Editar Dependente'
                            : 'Adicionar Dependente'}
                    </h3>

                    <div className="flex flex-col gap-4">
                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text">
                                    Nome <span className="text-error">*</span>
                                </span>
                            </label>
                            <input
                                type="text"
                                placeholder="Nome completo"
                                className="input input-bordered w-full"
                                value={dependentForm.name}
                                onChange={e =>
                                    setDependentForm(prev => ({
                                        ...prev,
                                        name: e.target.value,
                                    }))
                                }
                            />
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text">CPF</span>
                                </label>
                                <input
                                    type="text"
                                    placeholder="000.000.000-00"
                                    className="input input-bordered w-full"
                                    value={dependentForm.cpf}
                                    onChange={e =>
                                        setDependentForm(prev => ({
                                            ...prev,
                                            cpf: e.target.value,
                                        }))
                                    }
                                    maxLength={14}
                                />
                            </div>

                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text">Data de nascimento</span>
                                </label>
                                <input
                                    type="date"
                                    className="input input-bordered w-full"
                                    value={dependentForm.birthDate}
                                    onChange={e =>
                                        setDependentForm(prev => ({
                                            ...prev,
                                            birthDate: e.target.value,
                                        }))
                                    }
                                />
                            </div>
                        </div>

                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text">
                                    Parentesco <span className="text-error">*</span>
                                </span>
                            </label>
                            <select
                                className="select select-bordered w-full"
                                value={dependentForm.relationship}
                                onChange={e =>
                                    setDependentForm(prev => ({
                                        ...prev,
                                        relationship: e.target.value,
                                    }))
                                }
                            >
                                <option value="">Selecione</option>
                                {Object.entries(RELATIONSHIP_LABELS).map(([k, v]) => (
                                    <option key={k} value={k}>
                                        {v}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="flex flex-wrap gap-4 pt-2">
                            <label className="label cursor-pointer gap-2">
                                <input
                                    type="checkbox"
                                    className="checkbox checkbox-sm checkbox-secondary"
                                    checked={dependentForm.irDeduction}
                                    onChange={e =>
                                        setDependentForm(prev => ({
                                            ...prev,
                                            irDeduction: e.target.checked,
                                        }))
                                    }
                                />
                                <span className="label-text">Dedução IR</span>
                            </label>
                            <label className="label cursor-pointer gap-2">
                                <input
                                    type="checkbox"
                                    className="checkbox checkbox-sm checkbox-secondary"
                                    checked={dependentForm.familySalary}
                                    onChange={e =>
                                        setDependentForm(prev => ({
                                            ...prev,
                                            familySalary: e.target.checked,
                                        }))
                                    }
                                />
                                <span className="label-text">Salário família</span>
                            </label>
                            <label className="label cursor-pointer gap-2">
                                <input
                                    type="checkbox"
                                    className="checkbox checkbox-sm checkbox-secondary"
                                    checked={dependentForm.healthPlan}
                                    onChange={e =>
                                        setDependentForm(prev => ({
                                            ...prev,
                                            healthPlan: e.target.checked,
                                        }))
                                    }
                                />
                                <span className="label-text">Plano de saúde</span>
                            </label>
                        </div>
                    </div>

                    <div className="modal-action">
                        <button
                            className="btn btn-ghost"
                            onClick={closeDependentModal}
                            type="button"
                        >
                            Cancelar
                        </button>
                        <button
                            className="btn btn-secondary"
                            onClick={handleSaveDependent}
                            disabled={savingDependent}
                            type="button"
                        >
                            {savingDependent ? (
                                <span className="loading loading-spinner loading-sm" />
                            ) : editingDependent ? (
                                'Atualizar'
                            ) : (
                                'Salvar'
                            )}
                        </button>
                    </div>
                </div>
                <form method="dialog" className="modal-backdrop">
                    <button onClick={closeDependentModal}>fechar</button>
                </form>
            </dialog>

            {/* Terminate Modal */}
            <dialog
                className="modal"
                open={showTerminateModal}
            >
                <div className="modal-box w-11/12 max-w-md">
                    <h3 className="font-bold text-lg mb-4">
                        Desligar Funcionário
                    </h3>
                    <p className="text-sm text-base-content/60 mb-4">
                        Informe os dados do desligamento de{' '}
                        <strong>{employee.name}</strong>.
                    </p>

                    <div className="flex flex-col gap-4">
                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text">
                                    Tipo de desligamento{' '}
                                    <span className="text-error">*</span>
                                </span>
                            </label>
                            <select
                                className="select select-bordered w-full"
                                value={terminateForm.terminationType}
                                onChange={e =>
                                    setTerminateForm(prev => ({
                                        ...prev,
                                        terminationType: e.target.value,
                                    }))
                                }
                            >
                                <option value="">Selecione</option>
                                {Object.entries(TERMINATION_TYPE_LABELS).map(([k, v]) => (
                                    <option key={k} value={k}>
                                        {v}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text">
                                    Data de desligamento{' '}
                                    <span className="text-error">*</span>
                                </span>
                            </label>
                            <input
                                type="date"
                                className="input input-bordered w-full"
                                value={terminateForm.terminationDate}
                                onChange={e =>
                                    setTerminateForm(prev => ({
                                        ...prev,
                                        terminationDate: e.target.value,
                                    }))
                                }
                            />
                        </div>

                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text">Motivo</span>
                            </label>
                            <textarea
                                className="textarea textarea-bordered w-full"
                                placeholder="Descreva o motivo do desligamento (opcional)"
                                rows={3}
                                value={terminateForm.terminationReason}
                                onChange={e =>
                                    setTerminateForm(prev => ({
                                        ...prev,
                                        terminationReason: e.target.value,
                                    }))
                                }
                            />
                        </div>
                    </div>

                    <div className="modal-action">
                        <button
                            className="btn btn-ghost"
                            onClick={() => setShowTerminateModal(false)}
                            type="button"
                        >
                            Cancelar
                        </button>
                        <button
                            className="btn btn-error"
                            onClick={handleTerminate}
                            disabled={
                                terminating ||
                                !terminateForm.terminationType ||
                                !terminateForm.terminationDate
                            }
                            type="button"
                        >
                            {terminating ? (
                                <span className="loading loading-spinner loading-sm" />
                            ) : (
                                'Confirmar desligamento'
                            )}
                        </button>
                    </div>
                </div>
                <form method="dialog" className="modal-backdrop">
                    <button onClick={() => setShowTerminateModal(false)}>
                        fechar
                    </button>
                </form>
            </dialog>

            {/* Delete Dependent Confirm */}
            <ConfirmModal
                isOpen={showDeleteDependentModal}
                onClose={() => {
                    setShowDeleteDependentModal(false)
                    setDependentToDelete(null)
                }}
                onConfirm={handleDeleteDependent}
                title="Excluir dependente"
                message={`Tem certeza de que deseja excluir o dependente "${dependentToDelete?.name}"? Esta ação não pode ser desfeita.`}
                loading={deletingDependent}
                confirmButtonText="Excluir"
            />

            {/* Unlink User Confirm */}
            <ConfirmModal
                isOpen={showUnlinkModal}
                onClose={() => setShowUnlinkModal(false)}
                onConfirm={handleUnlinkUser}
                title="Desvincular usuário"
                message={`Tem certeza de que deseja desvincular o usuário "${employee?.user?.name}" deste funcionário? O usuário continuará existindo e podendo acessar o sistema.`}
                loading={unlinkingUser}
                confirmButtonText="Desvincular"
            />

            {/* Unlink Platform Account Confirm */}
            <ConfirmModal
                isOpen={showUnlinkAccountModal}
                onClose={() => setShowUnlinkAccountModal(false)}
                onConfirm={handleUnlinkPlatformAccount}
                title="Desvincular conta da plataforma"
                message="Sem a conta vinculada, o salário deste funcionário passa a ser pago pelo PIX ou pelos dados bancários do cadastro, com a tarifa da transferência. Deseja continuar?"
                loading={unlinkingAccount}
                confirmButtonText="Desvincular"
            />
        </div>
    )
}
